// 分類 / 品牌 — managed lists (the Woo app can't reorder categories or touch
// brands at all). Categories nest (parent), reorder with ↑ ↓, carry an icon +
// colour; both can be renamed, merged and deleted. A DB trigger keeps every
// product's label in step, so a rename shows everywhere at once.
// Also exports the pickers used by the product editor (search + inline create).
import { useMemo, useState } from "react";
import { zhEn } from "./posTier.js";
import { Plus, ChevronUp, ChevronDown, Search, Check, GitMerge, Trash2, ImagePlus } from "lucide-react";
import { C, Btn, Chip, Card, Sheet, Label, Field, Toggle, Empty, inputStyle } from "./posUi.jsx";
import { posErrorText, newId } from "./posMath.js";
import { saveCategory, deleteCategory, saveBrand, deleteBrand, mergeTaxonomy, patchCategory, patchBrand, uploadTaxonomyImage } from "../../lib/api.js";

const ICONS = ["🛍️", "🥣", "🦴", "🧴", "🧸", "🦮", "👕", "🐱", "🐶", "💊", "🛁", "✂️", "🎀", "🏠", "🐾", "⭐"];
// A category icon is either an emoji or an uploaded image URL.
export const isImageIcon = (v) => /^https:\/\//.test(v || "");
export function CatIcon({ icon, fallback = "🗂️", size = 20 }) {
  if (isImageIcon(icon)) return <img src={icon} alt="" style={{ width: size, height: size, objectFit: "contain", borderRadius: 4, display: "block" }} />;
  return <>{icon || fallback}</>;
}

const COLORS = ["", "#FF8FA3", "#F59E0B", "#10B981", "#3B82F6", "#8B5CF6", "#EC4899", "#64748B"];

// Depth-first order with depth, so children sit under their parent.
export function categoryTree(categories) {
  const kids = {};
  for (const c of categories) (kids[c.parent_id || ""] = kids[c.parent_id || ""] || []).push(c);
  for (const k of Object.keys(kids)) kids[k].sort((a, b) => (a.sort_order - b.sort_order) || a.name.localeCompare(b.name));
  const out = [];
  const seen = new Set();
  const walk = (pid, depth) => {
    for (const c of kids[pid] || []) {
      if (seen.has(c.id)) continue;
      seen.add(c.id);
      out.push({ ...c, depth });
      walk(c.id, depth + 1);
    }
  };
  walk("", 0);
  for (const c of categories) if (!seen.has(c.id)) out.push({ ...c, depth: 0 });   // orphans (parent deleted)
  return out;
}
export const categoryPath = (categories, id) => {
  const byId = Object.fromEntries(categories.map(c => [c.id, c]));
  const parts = [];
  let c = byId[id], guard = 0;
  while (c && guard++ < 8) { parts.unshift(c.name); c = byId[c.parent_id]; }
  return parts.join(" › ");
};
export const descendantsOf = (categories, id) => {
  const out = new Set([id]);
  let grew = true;
  while (grew) { grew = false; for (const c of categories) if (c.parent_id && out.has(c.parent_id) && !out.has(c.id)) { out.add(c.id); grew = true; } }
  return out;
};

export default function Taxonomy({ tenant, kind, items, setItems, products, onProductsChanged, toast }) {
  const isCat = kind === "category";
  const [edit, setEdit] = useState(null);
  const [merge, setMerge] = useState(null);
  const [q, setQ] = useState("");
  const counts = useMemo(() => {
    const m = {};
    for (const p of products) { const k = isCat ? p.categoryId : p.brandId; if (k) m[k] = (m[k] || 0) + 1; }
    return m;
  }, [products, isCat]);
  const rows = useMemo(() => {
    const list = isCat ? categoryTree(items) : [...items].sort((a, b) => (a.sort_order - b.sort_order) || a.name.localeCompare(b.name));
    const s = q.trim().toLowerCase();
    return s ? list.filter(x => x.name.toLowerCase().includes(s)) : list;
  }, [items, isCat, q]);
  const save = isCat ? saveCategory : saveBrand;
  const unassigned = products.filter(p => !(isCat ? p.categoryId : p.brandId)).length;

  // Swap with the neighbour among siblings, then renumber the siblings 0..n.
  // Siblings come from the full list (not the search-filtered one); only
  // sort_order is written, so a stale name can't undo a rename elsewhere.
  const move = async (row, dir) => {
    const full = isCat ? categoryTree(items) : [...items].sort((a, b) => (a.sort_order - b.sort_order) || a.name.localeCompare(b.name));
    const sibs = full.filter(x => (x.parent_id || "") === (row.parent_id || ""));
    const i = sibs.findIndex(x => x.id === row.id), j = i + dir;
    if (j < 0 || j >= sibs.length) return;
    const order = [...sibs]; [order[i], order[j]] = [order[j], order[i]];
    const changed = order.map((x, k) => ({ ...x, sort_order: k })).filter((x, k) => sibs.find(y => y.id === x.id).sort_order !== k);
    setItems(list => list.map(x => { const c = changed.find(y => y.id === x.id); return c ? { ...x, sort_order: c.sort_order } : x; }));
    try { for (const c of changed) await (isCat ? patchCategory : patchBrand)(c.id, { sort_order: c.sort_order }); }
    catch (e) { toast?.(posErrorText(e), "error"); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1, position: "relative" }}>
          <Search size={18} color={C.faint} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={isCat ? zhEn("搜尋分類", "Search categories") : zhEn("搜尋品牌", "Search brands")} style={{ ...inputStyle, paddingLeft: 38, borderRadius: 14 }} />
        </div>
        <Btn onClick={() => setEdit({ id: null, name: "", parent_id: null, icon: "", color: "", show_online: true, logo_url: "", description: "" })} style={{ padding: "0 16px" }}><Plus size={18} /> {zhEn("新增", "Add")}</Btn>
      </div>
      <p style={{ fontSize: 12.5, fontWeight: 700, color: C.sub, margin: "0 4px 10px" }}>
        {items.length} {zhEn("個", "")}{isCat ? zhEn("分類", "categories") : zhEn("品牌", "brands")}{unassigned ? " · " + unassigned + zhEn(" 件商品未", " products with no ") + (isCat ? zhEn("分類", "category") : zhEn("設品牌", "brand")) : ""}{isCat ? zhEn(" · ↑↓ 排次序（收銀分類跟呢個次序）", " · ↑↓ to reorder (the till follows this order)") : ""}
      </p>
      {rows.length === 0 ? <Empty icon={isCat ? "🗂️" : "🏷️"} title={isCat ? zhEn("未有分類", "No categories yet") : zhEn("未有品牌", "No brands yet")} sub={zhEn("撳「新增」，或者喺商品度直接打名就會自動建立", "Tap \"Add\", or just type a name on a product and it's created automatically")} />
        : rows.map(r => (
          <Card key={r.id} style={{ padding: "10px 12px", marginBottom: 8, marginLeft: (r.depth || 0) * 22, display: "flex", alignItems: "center", gap: 10 }}>
            {isCat
              ? <div style={{ width: 38, height: 38, borderRadius: 12, background: r.color ? r.color + "22" : C.accentSoft, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, flexShrink: 0, border: r.color ? "2px solid " + r.color : "none", overflow: "hidden" }}><CatIcon icon={r.icon} fallback={r.depth ? "↳" : "🗂️"} size={26} /></div>
              : r.logo_url ? <img src={r.logo_url} alt="" style={{ width: 38, height: 38, borderRadius: 12, objectFit: "contain", background: "#fff", border: "1px solid " + C.line }} />
              : <div style={{ width: 38, height: 38, borderRadius: 12, background: C.accentSoft, color: C.accent, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900 }}>{r.name.slice(0, 1)}</div>}
            <div style={{ flex: 1, minWidth: 0 }} onClick={() => setEdit(r)}>
              <p style={{ margin: 0, fontWeight: 900, color: C.ink, fontSize: 14.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</p>
              <p style={{ margin: 0, fontWeight: 700, color: C.sub, fontSize: 12 }}>{counts[r.id] || 0} {zhEn("件商品", "products")}{isCat && r.show_online === false ? zhEn(" · 網店隱藏", " · hidden online") : ""}</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <button type="button" aria-label={zhEn("上移", "Move up")} onClick={() => move(r, -1)} style={arrowBtn}><ChevronUp size={16} /></button>
              <button type="button" aria-label={zhEn("下移", "Move down")} onClick={() => move(r, 1)} style={arrowBtn}><ChevronDown size={16} /></button>
            </div>
          </Card>
        ))}

      {edit && <EditSheet isCat={isCat} row={edit} items={items} tenant={tenant} count={edit.id ? counts[edit.id] || 0 : 0}
        onClose={() => setEdit(null)} onMerge={() => { setMerge(edit); setEdit(null); }}
        onSaved={(row, removed) => { setItems(list => removed ? list.filter(x => x.id !== row.id).map(x => x.parent_id === row.id ? { ...x, parent_id: null } : x) : list.some(x => x.id === row.id) ? list.map(x => x.id === row.id ? row : x) : [...list, row]); setEdit(null); onProductsChanged?.(); }} />}
      {merge && <MergeSheet isCat={isCat} from={merge} items={items} tenant={tenant} count={counts[merge.id] || 0}
        onClose={() => setMerge(null)}
        onDone={(n, into) => { setItems(list => list.filter(x => x.id !== merge.id).map(x => x.parent_id === merge.id ? { ...x, parent_id: into } : x)); setMerge(null); toast?.(zhEn("已合併 · ", "Merged · ") + n + zhEn(" 件商品", " products")); onProductsChanged?.(); }} />}
    </div>
  );
}
function UploadBtn({ busy, label, onFile }) {
  return (
    <label style={{ height: 40, padding: "0 12px", borderRadius: 12, border: "2px dashed " + C.line, background: "#fff", color: C.accent, fontWeight: 900, fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
      <input type="file" accept="image/*" disabled={busy} style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; onFile(f); }} />
      {busy ? zhEn("上載中…", "Uploading…") : <><ImagePlus size={16} />{label}</>}
    </label>
  );
}

const arrowBtn = { border: "none", background: "transparent", color: C.sub, width: 30, height: 22, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" };

function EditSheet({ isCat, row, items, tenant, count, onClose, onSaved, onMerge }) {
  const [r, setR] = useState(row);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [upBusy, setUpBusy] = useState(false);
  const set = (p) => setR(x => ({ ...x, ...p }));
  const upload = async (file, field) => {
    if (!file) return;
    setUpBusy(true); setErr("");
    try { const url = await uploadTaxonomyImage(tenant.id, file, isCat ? "category-icon" : "brand-logo"); set({ [field]: url }); }
    catch (e) { setErr(zhEn("上載失敗：", "Upload failed: ") + (e?.message || e)); }
    finally { setUpBusy(false); }
  };
  const blocked = isCat && r.id ? descendantsOf(items, r.id) : new Set();
  const parents = isCat ? categoryTree(items).filter(c => !blocked.has(c.id)) : [];
  const save = async () => {
    if (!r.name.trim()) { setErr(zhEn("請輸入名稱", "Enter a name")); return; }
    setBusy(true); setErr("");
    try {
      const base = { id: r.id || newId(), tenant_id: tenant.id, name: r.name.trim(), sort_order: r.sort_order ?? items.length };
      const saved = isCat
        ? await saveCategory({ ...base, parent_id: r.parent_id || null, icon: r.icon || "", color: r.color || "", show_online: r.show_online !== false })
        : await saveBrand({ ...base, logo_url: (r.logo_url || "").trim(), description: r.description || "" });
      onSaved(saved);
    } catch (e) { setErr(/uniq|duplicate/.test(String(e?.message)) ? zhEn("已經有同名嘅", "There is already a ") + (isCat ? zhEn("分類", "category with that name") : zhEn("品牌", "brand with that name")) : posErrorText(e)); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(zhEn("刪除「", "Delete \"") + r.name + zhEn("」？", "\"?") + (count ? count + zhEn(" 件商品會變成", " products will become ") + (isCat ? zhEn("未分類", "uncategorised") : zhEn("冇品牌", "brandless")) + zhEn("。", ". ") : ""))) return;
    setBusy(true);
    try { await (isCat ? deleteCategory : deleteBrand)(r.id); onSaved(r, true); }
    catch (e) { setErr(posErrorText(e)); setBusy(false); }
  };
  return (
    <Sheet open onClose={busy ? undefined : onClose} title={(r.id ? zhEn("編輯", "Edit ") : zhEn("新增", "Add")) + (isCat ? zhEn("分類", "category") : zhEn("品牌", "brand"))}
      footer={<div>
        {err && <p style={{ color: C.red, fontWeight: 800, fontSize: 13, textAlign: "center", margin: "0 0 8px" }}>{err}</p>}
        <div style={{ display: "flex", gap: 8 }}>
          {r.id && <Btn kind="danger" onClick={del} disabled={busy}><Trash2 size={18} /></Btn>}
          {r.id && <Btn kind="ghost" onClick={onMerge} disabled={busy}><GitMerge size={18} /> {zhEn("合併", "Merge")}</Btn>}
          <Btn style={{ flex: 1 }} onClick={save} disabled={busy || upBusy}>{busy ? zhEn("儲存中…", "Saving…") : zhEn("儲存", "Save")}</Btn>
        </div>
      </div>}>
      <Field label={zhEn("名稱", "Name")} value={r.name} onChange={e => set({ name: e.target.value })} autoFocus placeholder={isCat ? zhEn("例如 小食", "e.g. Treats") : zhEn("例如 PawsCare", "e.g. PawsCare")} />
      {r.id && count > 0 && <p style={{ fontSize: 12, fontWeight: 700, color: C.sub, margin: "-6px 2px 12px" }}>{zhEn("改名會即刻更新", "Renaming updates everywhere straight away")} {count} {zhEn("件商品", "products")}</p>}
      {isCat ? (
        <>
          <Label>{zhEn("上層分類", "Parent category")}</Label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
            <Chip on={!r.parent_id} onClick={() => set({ parent_id: null })}>{zhEn("（冇，頂層）", "(none, top level)")}</Chip>
            {parents.map(c => <Chip key={c.id} on={r.parent_id === c.id} onClick={() => set({ parent_id: c.id })}>{"　".repeat(c.depth)}{c.name}</Chip>)}
          </div>
          <Label>{zhEn("圖示", "Icon")}</Label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
            <Chip on={!r.icon} onClick={() => set({ icon: "" })}>{zhEn("無", "None")}</Chip>
            {ICONS.map(i => <button key={i} type="button" onClick={() => set({ icon: i })} style={{ width: 40, height: 40, borderRadius: 12, border: "2px solid " + (r.icon === i ? C.accent : C.line), background: "#fff", fontSize: 20, cursor: "pointer" }}>{i}</button>)}
            {isImageIcon(r.icon) && <span style={{ width: 40, height: 40, borderRadius: 12, border: "2px solid " + C.accent, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}><CatIcon icon={r.icon} size={30} /></span>}
            <UploadBtn busy={upBusy} label={zhEn("上載圖示", "Upload icon")} onFile={f => upload(f, "icon")} />
          </div>
          <Label>{zhEn("顏色", "Colour")}</Label>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            {COLORS.map(c => <button key={c || "none"} type="button" aria-label={c || zhEn("無", "None")} onClick={() => set({ color: c })} style={{ width: 32, height: 32, borderRadius: 99, border: "3px solid " + (r.color === c ? C.ink : "#fff"), background: c || "#F3F2F8", boxShadow: "0 0 0 1px " + C.line, cursor: "pointer" }} />)}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 2px" }}>
            <span style={{ flex: 1, fontWeight: 900, color: C.ink, fontSize: 14 }}>{zhEn("網店顯示呢個分類", "Show this category on the online shop")}</span>
            <Toggle on={r.show_online !== false} onChange={v => set({ show_online: v })} />
          </div>
        </>
      ) : (
        <>
          <Label>Logo</Label>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            {r.logo_url && <img src={r.logo_url} alt="" style={{ width: 56, height: 56, borderRadius: 14, objectFit: "contain", background: "#fff", border: "1px solid " + C.line }} />}
            <UploadBtn busy={upBusy} label={r.logo_url ? zhEn("換 Logo", "Change logo") : zhEn("上載 Logo", "Upload logo")} onFile={f => upload(f, "logo_url")} />
            {r.logo_url && <Btn kind="ghost" small onClick={() => set({ logo_url: "" })}>{zhEn("移除", "Remove")}</Btn>}
          </div>
          <Field value={r.logo_url || ""} onChange={e => set({ logo_url: e.target.value })} placeholder={zhEn("或者貼圖片網址 https://…", "Or paste an image address https://…")} />
          <Label>{zhEn("簡介", "Description")}</Label>
          <textarea value={r.description || ""} onChange={e => set({ description: e.target.value })} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
        </>
      )}
    </Sheet>
  );
}

function MergeSheet({ isCat, from, items, tenant, count, onClose, onDone }) {
  const [into, setInto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Never into one of its own sub-categories (that would make a loop).
  const blocked = isCat ? descendantsOf(items, from.id) : new Set([from.id]);
  const opts = (isCat ? categoryTree(items) : items).filter(x => !blocked.has(x.id));
  return (
    <Sheet open onClose={busy ? undefined : onClose} title={zhEn("合併「", "Merge \"") + from.name + zhEn("」", "\"")}
      footer={<div>
        {err && <p style={{ color: C.red, fontWeight: 800, fontSize: 13, textAlign: "center", margin: "0 0 8px" }}>{err}</p>}
        <Btn style={{ width: "100%" }} disabled={!into || busy} onClick={async () => {
          setBusy(true); setErr("");
          try { onDone(await mergeTaxonomy({ tenantId: tenant.id, kind: isCat ? "category" : "brand", fromId: from.id, intoId: into }), into); }
          catch (e) { setErr(posErrorText(e)); setBusy(false); }
        }}>{busy ? zhEn("合併中…", "Merging…") : zhEn("合併 · 搬走 ", "Merge · move ") + count + zhEn(" 件商品", " products")}</Btn>
      </div>}>
      <p style={{ fontSize: 13, fontWeight: 700, color: C.sub, margin: "0 2px 12px" }}>{zhEn("「", "\"")}{from.name}{zhEn("」嘅商品會搬去揀選嘅", "\" items will move to the chosen ")}{isCat ? zhEn("分類（子分類都會跟埋）", "category (subcategories come along)") : zhEn("品牌", "brand")}{zhEn("，然後刪除「", ", then \"")}{from.name}{zhEn("」。適合合併重複或者打錯字嘅名。", "\" is deleted. Good for merging duplicate or misspelled names.")}</p>
      {opts.map(x => (
        <button key={x.id} type="button" onClick={() => setInto(x.id)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "11px 12px", marginBottom: 6, borderRadius: 14, border: "1.5px solid " + (into === x.id ? C.accent : C.line), background: into === x.id ? C.accentSoft : "#fff", fontFamily: "inherit", fontWeight: 800, fontSize: 14, color: C.ink, cursor: "pointer", textAlign: "left", paddingLeft: 12 + (x.depth || 0) * 18 }}>
          <span style={{ flex: 1 }}>{x.name}</span>{into === x.id && <Check size={18} color={C.accent} />}
        </button>
      ))}
    </Sheet>
  );
}

// Picker for the product editor / bulk edit. Searchable, hierarchical,
// inline create (with parent for categories). onPick(row | null).
export function TaxonomyPicker({ open, onClose, kind, items, setItems, tenant, value, onPick, allowNone = true }) {
  const isCat = kind === "category";
  const [q, setQ] = useState("");
  const [parent, setParent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const list = useMemo(() => {
    const all = isCat ? categoryTree(items) : [...items].sort((a, b) => a.name.localeCompare(b.name));
    const s = q.trim().toLowerCase();
    return s ? all.filter(x => x.name.toLowerCase().includes(s)) : all;
  }, [items, isCat, q]);
  const exact = items.some(x => x.name.toLowerCase() === q.trim().toLowerCase() && (!isCat || (x.parent_id || null) === parent));
  const create = async () => {
    setBusy(true); setErr("");
    try {
      const row = isCat
        ? await saveCategory({ id: newId(), tenant_id: tenant.id, name: q.trim(), parent_id: parent, sort_order: items.length })
        : await saveBrand({ id: newId(), tenant_id: tenant.id, name: q.trim(), sort_order: items.length });
      setItems(xs => [...xs, row]); setQ(""); onPick(row);
    } catch (e) { setErr(/uniq|duplicate/.test(String(e?.message)) ? zhEn("已經有同名", "That name already exists") : posErrorText(e)); }
    finally { setBusy(false); }
  };
  if (!open) return null;
  return (
    <Sheet open onClose={onClose} title={isCat ? zhEn("揀分類", "Choose category") : zhEn("揀品牌", "Choose brand")}>
      <div style={{ position: "relative", marginBottom: 10 }}>
        <Search size={18} color={C.faint} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
        <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={zhEn("搜尋或輸入新", "Search or enter a new ") + (isCat ? zhEn("分類", "category") : zhEn("品牌", "brand"))} style={{ ...inputStyle, paddingLeft: 38 }} />
      </div>
      {q.trim() && !exact && (
        <div style={{ background: C.accentSoft, borderRadius: 14, padding: 10, marginBottom: 10 }}>
          {isCat && items.length > 0 && (
            <div className="pos-noscroll" style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 8 }}>
              <Chip on={!parent} onClick={() => setParent(null)}>{zhEn("頂層", "Top level")}</Chip>
              {categoryTree(items).filter(c => c.depth === 0).map(c => <Chip key={c.id} on={parent === c.id} onClick={() => setParent(c.id)}>{zhEn("喺「", "Under \"")}{c.name}{zhEn("」之下", "\"")}</Chip>)}
            </div>
          )}
          <Btn small style={{ width: "100%" }} disabled={busy} onClick={create}><Plus size={16} /> {zhEn("新增", "Add")}{isCat ? zhEn("分類", "Categories") : zhEn("品牌", "Brands")}{zhEn("「", "\"")}{q.trim()}{zhEn("」", "\"")}</Btn>
          {err && <p style={{ color: C.red, fontWeight: 800, fontSize: 12.5, margin: "6px 0 0" }}>{err}</p>}
        </div>
      )}
      {allowNone && <button type="button" onClick={() => onPick(null)} style={pickRow(!value)}><span style={{ flex: 1 }}>{isCat ? zhEn("未分類", "uncategorised") : zhEn("冇品牌", "brandless")}</span>{!value && <Check size={18} color={C.accent} />}</button>}
      {list.map(x => (
        <button key={x.id} type="button" onClick={() => onPick(x)} style={{ ...pickRow(value === x.id), paddingLeft: 12 + (x.depth || 0) * 20 }}>
          {isCat && <span style={{ fontSize: 16, display: "inline-flex" }}><CatIcon icon={x.icon} fallback={x.depth ? "↳" : "🗂️"} size={18} /></span>}
          <span style={{ flex: 1 }}>{x.name}</span>{value === x.id && <Check size={18} color={C.accent} />}
        </button>
      ))}
    </Sheet>
  );
}
const pickRow = (on) => ({ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "12px", marginBottom: 6, borderRadius: 14, border: "1.5px solid " + (on ? C.accent : C.line), background: on ? C.accentSoft : "#fff", fontFamily: "inherit", fontWeight: 800, fontSize: 14.5, color: C.ink, cursor: "pointer", textAlign: "left" });

